import { Badge, makeStyles, tokens, type BadgeProps } from '@fluentui/react-components';
import { AUDIT_ACTIONS, type AuditAction } from '@pr-app/shared';

/**
 * An audit action, as a dot **and** a word — the treatment `StatusBadge` gives a
 * request status (#54), applied to the admin audit log.
 *
 * Wireframe sheet 06.1, frame A4 draws every row's Action as a badge, worded for
 * a human rather than printed as an enum: "PR created", "Approved", "Setting
 * changed", "Resubmitted", "Rejected", "Reviewer added", "Submitted", "PR
 * failed". A4 note 3 is why the machine-written rows carry one too — *"System
 * actions sit alongside human ones. PR-created and PR-failed are audit events
 * with the same weight as an approval — that is how a stuck request is found."*
 * A `PR_FAILED` rendered as raw enum text in a column of raw enum text is
 * exactly the row that gets scrolled past.
 *
 * Colour never carries the meaning alone: high contrast has no colour at all
 * (sheet 08.1 note 1), so the word is what survives and the outline keeps the
 * pill visible once the palette background collapses to black.
 */
const ACTION_LABELS: Record<AuditAction, string> = {
  CREATED: 'Created',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RESUBMITTED: 'Resubmitted',
  REVIEWER_EDITED: 'Edited by reviewer',
  PR_CREATED: 'PR created',
  PR_FAILED: 'PR failed',
  PR_ATTACHMENTS_FAILED: 'Attachments not linked',
  PR_SUBMIT_FAILED: 'Not submitted to workflow',
  REQUESTER_NOTIFIED: 'Requester notified',
  DELETED: 'Deleted',
  REVIEWER_ADDED: 'Reviewer added',
  REVIEWER_REMOVED: 'Reviewer removed',
  ADMIN_ADDED: 'Administrator added',
  ADMIN_REMOVED: 'Administrator removed',
  SETTING_CHANGED: 'Setting changed',
  WITHDRAWN: 'Withdrawn',
  DRAFT_EXPIRED: 'Draft expired',
  // #235 — an automatic notification (PR-created or rejection) failed to
  // send. Neither the request nor its status is affected; see the comment
  // for which notification and why.
  NOTIFICATION_FAILED: 'Notification failed',
};

/**
 * Green where something reached the end it was meant to (approved, the
 * requester told), red where it did not (rejected, failed, deleted, a reviewer
 * taken out of the group), and informative for the lifecycle and administrative
 * rows that are neither — a settings change is a fact, not an outcome, and
 * colouring it would drown the two rows that matter. `PR_CREATED` is brand,
 * matching `StatusBadge`, so the same event is the same colour wherever it
 * appears in the app.
 */
const ACTION_COLORS: Record<AuditAction, BadgeProps['color']> = {
  CREATED: 'informative',
  SUBMITTED: 'informative',
  APPROVED: 'success',
  REJECTED: 'danger',
  RESUBMITTED: 'informative',
  REVIEWER_EDITED: 'informative',
  PR_CREATED: 'brand',
  PR_FAILED: 'danger',
  // Warning, not danger: the requisition was created and the request is
  // PR_CREATED — what is missing is its files (#132). Colouring it like a
  // failure would say the PR did not happen, which is the confusion this
  // action exists to avoid.
  PR_ATTACHMENTS_FAILED: 'warning',
  // Warning for the same reason: the requisition exists; only the submit step is missing.
  PR_SUBMIT_FAILED: 'warning',
  REQUESTER_NOTIFIED: 'success',
  DELETED: 'danger',
  REVIEWER_ADDED: 'informative',
  REVIEWER_REMOVED: 'danger',
  ADMIN_ADDED: 'informative',
  // Same treatment as REVIEWER_REMOVED: someone's access just narrowed.
  ADMIN_REMOVED: 'danger',
  SETTING_CHANGED: 'informative',
  // The requester's own choice, not a failure — same informative treatment as
  // RESUBMITTED: a lifecycle event, not an outcome to colour as good or bad.
  WITHDRAWN: 'informative',
  // Same treatment as DELETED: the draft is gone either way, and colouring
  // the automatic case differently would suggest it matters less.
  DRAFT_EXPIRED: 'danger',
  // Warning, not danger, for the same reason PR_ATTACHMENTS_FAILED is: the
  // thing itself (the approval, the rejection, the PR) succeeded — only the
  // notification about it did not.
  NOTIFICATION_FAILED: 'warning',
};

const KNOWN_ACTIONS = new Set<string>(AUDIT_ACTIONS);

function isAuditAction(action: string): action is AuditAction {
  return KNOWN_ACTIONS.has(action);
}

const useStyles = makeStyles({
  // `currentColor` rather than a palette token: the dot then tracks whatever
  // the badge's colour resolves to, including plain white in high contrast.
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: 'currentColor',
  },
});

/**
 * The trail is insert-only and holds rows written by every version of this app
 * that has ever run, so an action this build has no label for is a real
 * possibility. It keeps its raw value rather than rendering blank — the row
 * still reads, and the unfamiliar name is visible instead of swallowed.
 */
export function auditActionLabel(action: string): string {
  return isAuditAction(action) ? ACTION_LABELS[action] : action;
}

export function AuditActionBadge({ action }: { action: string }) {
  const styles = useStyles();

  return (
    <Badge
      appearance="outline"
      color={isAuditAction(action) ? ACTION_COLORS[action] : 'informative'}
      size="medium"
      // Decorative: the word beside it already carries the meaning, so
      // announcing the dot would only repeat it.
      icon={<span aria-hidden="true" className={styles.dot} />}
    >
      {auditActionLabel(action)}
    </Badge>
  );
}
